// src/contexts/ProductContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  getAllProducts, 
  getFeaturedProducts,
  getProductsByCategory,
  searchProducts
} from '../services/ProductService';
import { getAllCategories } from '../services/CategoryService';

// Create context
const ProductContext = createContext();

// Create provider
export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Load all products
        const productsData = await getAllProducts();
        setProducts(productsData);
        
        // Load categories
        const categoriesData = await getAllCategories();
        setCategories(categoriesData);
        
        // Load featured products
        const featuredData = await getFeaturedProducts();
        setFeaturedProducts(featuredData);
        
        setError(null);
      } catch (error) {
        console.error('Error loading initial product data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Get products by category
  const getProductsByCat = async (categoryId) => {
    try {
      setLoading(true);
      const categoryProducts = await getProductsByCategory(categoryId);
      return categoryProducts;
    } catch (error) {
      console.error('Error getting products by category:', error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Search for products
  const searchForProducts = async (searchTerm) => {
    try {
      setLoading(true);
      const searchResults = await searchProducts(searchTerm);
      return searchResults;
    } catch (error) {
      console.error('Error searching products:', error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Value to be provided to consumers
  const value = {
    products,
    categories,
    featuredProducts,
    loading,
    error,
    getProductsByCat,
    searchForProducts,
    refreshProducts: async () => {
      try {
        setLoading(true);
        const productsData = await getAllProducts();
        setProducts(productsData);
        const featuredData = await getFeaturedProducts();
        setFeaturedProducts(featuredData);
      } catch (error) {
        console.error('Error refreshing products:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

// Custom hook to use the product context
export const useProducts = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export default ProductContext;